import { apiClient } from "@nabble/sdk";
import { BASE } from "./http";
import type { Destination, DriveNode, DriveSpace, Listing, NodePatch, Person } from "./types";

const query = (params: Record<string, string>): string => new URLSearchParams(params).toString();

export const driveApi = {
  spaces: () => apiClient<DriveSpace[]>(`${BASE}/spaces`),
  root: (organizationId: string) => apiClient<Listing>(`${BASE}/folders/root?${query({ organizationId })}`),
  folder: (id: string) => apiClient<Listing>(`${BASE}/folders/${id}`),
  node: (id: string) => apiClient<DriveNode>(`${BASE}/nodes/${id}`),
  createFolder: (name: string, destination: Destination) =>
    apiClient<DriveNode>(`${BASE}/folders`, { method: "POST", body: { name, ...destination } }),
  update: (id: string, patch: NodePatch) => apiClient<DriveNode>(`${BASE}/nodes/${id}`, { method: "PATCH", body: patch }),
  remove: (id: string) => apiClient<void>(`${BASE}/nodes/${id}`, { method: "DELETE" }),
  search: (q: string, signal?: AbortSignal) => apiClient<DriveNode[]>(`${BASE}/search?${query({ q })}`, { signal }),
  people: (organizationId: string) => apiClient<Person[]>(`${BASE}/people?${query({ organizationId })}`),
};

/** Where a file's bytes come from (same origin, session cookie): a download, or inline for previews. */
export function contentUrl(id: string, inline = false): string {
  return `${BASE}/files/${id}/content${inline ? "?inline=true" : ""}`;
}

/**
 * Reads a file's bytes for a preview (a plain GET, so no CSRF token). `maxBytes` asks only for the
 * beginning of the file with a Range header.
 */
export async function fetchContent(id: string, signal: AbortSignal, maxBytes?: number): Promise<ArrayBuffer> {
  const headers: Record<string, string> = maxBytes ? { Range: `bytes=0-${maxBytes - 1}` } : {};
  const response = await fetch(contentUrl(id), { credentials: "same-origin", headers, signal });
  if (!response.ok) throw new Error(`content ${response.status}`);
  return response.arrayBuffer();
}
